// Editor matrix for a Current Staff Plan.
// Rows = (discipline × channel), columns = L1..L8. Each level cell is split
// into two side-by-side inputs: "On" (onshore FTE) and "Hub" (offshore +
// nearshore FTE rolled up — both are OGS hub roles per the model's shore
// tagging). Hub inputs carry a subtle amber tint to differentiate them from
// onshore without shouting (matching the Version 1 output table styling).
// Right and bottom show totals per row, column, and the grand total.
//
// Rows are the fixed structured (discipline × channel) set derived from the
// model — there is no add/remove-row control. Each row exposes its own Hub
// location selector so different rows can staff their Hub FTE at different
// hubs; rows left on "Default" inherit the plan-wide hub.

import { useMemo } from 'react';
import clsx from 'clsx';
import { resolveCellSplit } from '@rc/engine';
import type {
  LevelId,
  Model,
  StaffPlanCell,
  StaffPlanHubLocation,
} from '@rc/engine/types';

export interface StaffPlanGridProps {
  model: Model;
  cells: StaffPlanCell[];
  onChange: (cells: StaffPlanCell[]) => void;
  /** Per-row hub overrides keyed by `${disciplineId}::${channelId}`. */
  rowHubs?: Record<string, StaffPlanHubLocation>;
  /** Called when a row's Hub location selection changes. `undefined` clears
   *  the override so the row falls back to the plan default. */
  onRowHubChange?: (
    disciplineId: string,
    channelId: string,
    hub: StaffPlanHubLocation | undefined,
  ) => void;
  /** Optional label for the plan-wide default hub, e.g. "Manila (Offshore)".
   *  Shown as the fallback option in each row's Hub selector and as a hint in
   *  the column sub-headers so the editor knows where Hub headcount lives. */
  hubLabel?: string;
}

interface RowKey {
  disciplineId: string;
  channelId: string;
}

const ROW_KEY = (r: RowKey) => `${r.disciplineId}::${r.channelId}`;
const CELL_KEY = (disciplineId: string, channelId: string, levelId: LevelId) =>
  `${disciplineId}::${channelId}::${levelId}`;

const DEFAULT_HUB = '__default__';

/**
 * Default row set derived from the model:
 *   - Every discipline whose juniorRatio is keyed off `totalBudget` (or has
 *     no channel filter) gets ONE "all channels" row.
 *   - Investment / AdOps disciplines get one row per active channel.
 * This matches the Excel layout exactly.
 */
function defaultRowsForModel(model: Model): RowKey[] {
  const channels = (model.channels || []).filter((c) => c.active !== false);
  const out: RowKey[] = [];
  for (const d of model.disciplines || []) {
    if (d.active === false) continue;
    const filterIds = d.juniorRatio?.channelIdFilter;
    const cat = d.juniorRatio?.channelCategoryFilter;
    const driver = d.juniorRatio?.driver;
    if (driver === 'totalBudget' && !filterIds && !cat) {
      out.push({ disciplineId: d.id, channelId: 'all' });
      continue;
    }
    const eligible = channels.filter((ch) => {
      if (filterIds && filterIds.length > 0) return filterIds.includes(ch.id);
      if (cat) return ch.category === cat;
      return true;
    });
    if (eligible.length === 0) {
      out.push({ disciplineId: d.id, channelId: 'all' });
    } else {
      for (const ch of eligible) out.push({ disciplineId: d.id, channelId: ch.id });
    }
  }
  return out;
}

/** Round to 1 decimal to keep the editor stable — avoids the input flickering
 *  with floating point noise like 1.2000000000000002 after summing. */
function r1(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10) / 10;
}

export default function StaffPlanGrid({
  model,
  cells,
  onChange,
  rowHubs,
  onRowHubChange,
  hubLabel,
}: StaffPlanGridProps) {
  const levels = model.levels || [];
  const hubs = useMemo(() => model.ogsHubs ?? [], [model.ogsHubs]);

  const rows = useMemo<RowKey[]>(() => {
    const defaults = defaultRowsForModel(model);
    const seen = new Set(defaults.map(ROW_KEY));
    const extras: RowKey[] = [];
    for (const c of cells) {
      const k = ROW_KEY(c);
      if (!seen.has(k)) {
        seen.add(k);
        extras.push({ disciplineId: c.disciplineId, channelId: c.channelId });
      }
    }
    return [...defaults, ...extras];
  }, [cells, model]);

  const cellByKey = useMemo(() => {
    const map = new Map<string, StaffPlanCell>();
    for (const c of cells) {
      map.set(CELL_KEY(c.disciplineId, c.channelId, c.levelId), c);
    }
    return map;
  }, [cells]);

  const splitFor = (
    r: RowKey,
    levelId: LevelId,
  ): { on: number; hub: number; total: number } => {
    const c = cellByKey.get(CELL_KEY(r.disciplineId, r.channelId, levelId));
    if (!c) return { on: 0, hub: 0, total: 0 };
    return resolveCellSplit(c);
  };

  const discLabel = (id: string) =>
    (model.disciplines || []).find((d) => d.id === id)?.label || id;
  const chanLabel = (id: string) =>
    id === 'all' ? 'All channels' : (model.channels || []).find((c) => c.id === id)?.label || id;

  const setCellSplit = (
    r: RowKey,
    levelId: LevelId,
    side: 'on' | 'hub',
    n: number,
  ) => {
    const cur = splitFor(r, levelId);
    const nextOn = side === 'on' ? r1(n) : r1(cur.on);
    const nextHub = side === 'hub' ? r1(n) : r1(cur.hub);
    const total = r1(nextOn + nextHub);

    const next = [...cells];
    const idx = next.findIndex(
      (c) =>
        c.disciplineId === r.disciplineId &&
        c.channelId === r.channelId &&
        c.levelId === levelId,
    );
    if (idx >= 0) {
      if (total <= 0 && nextOn <= 0 && nextHub <= 0) {
        next.splice(idx, 1);
      } else {
        next[idx] = {
          ...next[idx],
          fte: total,
          onFte: nextOn,
          hubFte: nextHub,
        };
      }
    } else if (total > 0 || nextOn > 0 || nextHub > 0) {
      next.push({
        disciplineId: r.disciplineId,
        channelId: r.channelId,
        levelId,
        fte: total,
        onFte: nextOn,
        hubFte: nextHub,
      });
    }
    onChange(next);
  };

  const handleRowHub = (r: RowKey, value: string) => {
    if (!onRowHubChange) return;
    if (value === DEFAULT_HUB) {
      onRowHubChange(r.disciplineId, r.channelId, undefined);
      return;
    }
    const hub = hubs.find((h) => h.id === value);
    if (!hub) return;
    onRowHubChange(r.disciplineId, r.channelId, {
      hubId: hub.id,
      label: hub.label,
      shore: hub.shore,
    });
  };

  const rowHubValue = (r: RowKey): string => {
    const override = rowHubs?.[ROW_KEY(r)];
    if (override?.hubId && hubs.some((h) => h.id === override.hubId)) {
      return override.hubId;
    }
    return DEFAULT_HUB;
  };

  const rowSplit = (r: RowKey): { on: number; hub: number; total: number } => {
    let on = 0;
    let hub = 0;
    for (const l of levels) {
      const s = splitFor(r, l.id);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const disciplineSplit = (
    disciplineId: string,
  ): { on: number; hub: number; total: number } => {
    let on = 0;
    let hub = 0;
    for (const c of cells) {
      if (c.disciplineId !== disciplineId) continue;
      const s = resolveCellSplit(c);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const columnSplit = (levelId: LevelId): { on: number; hub: number; total: number } => {
    let on = 0;
    let hub = 0;
    for (const c of cells) {
      if (c.levelId !== levelId) continue;
      const s = resolveCellSplit(c);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const grand = cells.reduce(
    (acc, c) => {
      const s = resolveCellSplit(c);
      acc.on += s.on;
      acc.hub += s.hub;
      acc.total += s.total;
      return acc;
    },
    { on: 0, hub: 0, total: 0 },
  );

  const grouped = useMemo(() => {
    const byDisc = new Map<string, RowKey[]>();
    for (const r of rows) {
      const list = byDisc.get(r.disciplineId) || [];
      list.push(r);
      byDisc.set(r.disciplineId, list);
    }
    return Array.from(byDisc.entries());
  }, [rows]);

  const colSpanLevels = (levels.length || 0) * 2;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
            <tr>
              <th
                rowSpan={2}
                className="text-left align-bottom px-3 py-2 sticky left-0 bg-slate-50 min-w-[180px] border-b border-slate-200"
              >
                Discipline
              </th>
              <th
                rowSpan={2}
                className="text-left align-bottom px-3 py-2 min-w-[140px] border-b border-slate-200"
              >
                Channel
              </th>
              <th
                rowSpan={2}
                className="text-left align-bottom px-3 py-2 min-w-[150px] border-b border-slate-200"
              >
                Hub location
              </th>
              {levels.map((l) => (
                <th
                  key={l.id}
                  colSpan={2}
                  className="text-center px-1.5 pt-2 pb-1 tabular-nums font-semibold text-slate-600 border-b border-slate-100"
                >
                  {l.id}
                </th>
              ))}
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-slate-100/60 border-b border-slate-200"
              >
                On
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-amber-50/60 border-b border-slate-200"
              >
                Hub
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-slate-100/60 border-b border-slate-200"
              >
                Total
              </th>
            </tr>
            <tr className="text-[9px] tracking-wider text-slate-400">
              {levels.map((l) => (
                <SubHeaderPair key={l.id} hubLabel={hubLabel} />
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([disciplineId, discRows], gi) => (
              <DisciplineGroup
                key={disciplineId}
                disciplineId={disciplineId}
                disciplineLabel={discLabel(disciplineId)}
                rows={discRows}
                chanLabel={chanLabel}
                levels={levels}
                splitFor={splitFor}
                rowSplit={rowSplit}
                disciplineSplit={disciplineSplit(disciplineId)}
                onCellChange={setCellSplit}
                hubs={hubs}
                hubLabel={hubLabel}
                rowHubValue={rowHubValue}
                onRowHub={handleRowHub}
                colSpanLevels={colSpanLevels}
                first={gi === 0}
              />
            ))}
          </tbody>
          <tfoot className="bg-slate-100/60 text-xs uppercase tracking-wider text-slate-500 sticky bottom-0">
            <tr>
              <td className="px-3 py-2 font-semibold text-slate-700 border-t border-slate-200" colSpan={3}>
                Column total
              </td>
              {levels.map((l) => {
                const s = columnSplit(l.id);
                return (
                  <FragmentishCell key={l.id}>
                    <td
                      className="px-1.5 py-2 text-right tabular-nums font-semibold text-slate-700 border-l border-slate-100 border-t border-slate-200"
                    >
                      {s.on > 0 ? s.on.toFixed(1) : '·'}
                    </td>
                    <td
                      className="px-1.5 py-2 text-right tabular-nums font-semibold text-amber-900 bg-amber-50/40 border-t border-slate-200"
                    >
                      {s.hub > 0 ? s.hub.toFixed(1) : '·'}
                    </td>
                  </FragmentishCell>
                );
              })}
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700 border-t border-slate-200">
                {grand.on.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-900 bg-amber-50/60 border-t border-slate-200">
                {grand.hub.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-brand-purple-700 border-t border-slate-200">
                {grand.total.toFixed(1)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center gap-4 text-[10px] uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-slate-200 bg-white" />
          On — onshore FTE
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-amber-200 bg-amber-50/60" />
          Hub{hubLabel ? ` — default ${hubLabel}` : ' — offshore + nearshore FTE'}
        </span>
      </div>
    </div>
  );
}

function SubHeaderPair({ hubLabel }: { hubLabel?: string }) {
  return (
    <>
      <th className="font-medium text-right pb-2 pl-2 pr-1 border-b border-slate-200 border-l border-slate-100">
        On
      </th>
      <th
        className="font-medium text-right pb-2 pl-1 pr-2 border-b border-slate-200 bg-amber-50/40 text-amber-700/70"
        title={hubLabel || 'Offshore + nearshore'}
      >
        Hub
      </th>
    </>
  );
}

/** Fragment helper so adjacent siblings remain in the row without React keys. */
function FragmentishCell({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function DisciplineGroup({
  disciplineId,
  disciplineLabel,
  rows,
  chanLabel,
  levels,
  splitFor,
  rowSplit,
  disciplineSplit,
  onCellChange,
  hubs,
  hubLabel,
  rowHubValue,
  onRowHub,
  colSpanLevels,
  first,
}: {
  disciplineId: string;
  disciplineLabel: string;
  rows: RowKey[];
  chanLabel: (id: string) => string;
  levels: Model['levels'];
  splitFor: (r: RowKey, l: LevelId) => { on: number; hub: number; total: number };
  rowSplit: (r: RowKey) => { on: number; hub: number; total: number };
  disciplineSplit: { on: number; hub: number; total: number };
  onCellChange: (r: RowKey, l: LevelId, side: 'on' | 'hub', n: number) => void;
  hubs: NonNullable<Model['ogsHubs']>;
  hubLabel?: string;
  rowHubValue: (r: RowKey) => string;
  onRowHub: (r: RowKey, value: string) => void;
  colSpanLevels: number;
  first: boolean;
}) {
  return (
    <>
      <tr className={clsx('bg-slate-50/60', !first && 'border-t-2 border-slate-200')}>
        <td className="px-3 py-1.5 text-xs uppercase tracking-wider text-slate-600 font-semibold sticky left-0 bg-slate-50/60">
          {disciplineLabel}
        </td>
        <td
          className="px-3 py-1.5 text-xs text-slate-500"
          colSpan={colSpanLevels + 2}
        >
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-700 bg-slate-100/60">
          {disciplineSplit.on.toFixed(1)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-amber-900 bg-amber-50/60">
          {disciplineSplit.hub.toFixed(1)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-700 bg-slate-100/60">
          {disciplineSplit.total.toFixed(1)}
        </td>
      </tr>
      {rows.map((r) => {
        const rs = rowSplit(r);
        return (
          <tr
            key={ROW_KEY(r)}
            className="border-t border-slate-100 hover:bg-slate-50/40 transition-colors"
          >
            <td className="px-3 py-1.5 sticky left-0 bg-white text-slate-700 font-medium text-xs">
              <span className="pl-2 text-slate-400">↳</span> {disciplineLabel}
            </td>
            <td className="px-3 py-1.5 text-slate-500 text-xs">{chanLabel(r.channelId)}</td>
            <td className="px-2 py-1.5">
              <select
                className="input py-1 px-1.5 text-xs w-full max-w-[160px]"
                value={rowHubValue(r)}
                onChange={(e) => onRowHub(r, e.target.value)}
                title="Where this row's Hub FTE sits"
              >
                <option value={DEFAULT_HUB}>
                  Default{hubLabel ? ` · ${hubLabel}` : ''}
                </option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.label} ({h.shore})
                  </option>
                ))}
              </select>
            </td>
            {levels.map((l) => {
              const s = splitFor(r, l.id);
              return (
                <FragmentishCell key={l.id}>
                  <td className="px-1 py-1 align-middle min-w-[64px] border-l border-slate-100">
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      className="input py-1 px-1.5 w-full text-right text-xs tabular-nums"
                      value={s.on || ''}
                      placeholder="—"
                      onChange={(e) =>
                        onCellChange(
                          r,
                          l.id,
                          'on',
                          e.target.value === '' ? 0 : Number(e.target.value),
                        )
                      }
                    />
                  </td>
                  <td className="px-1 py-1 align-middle min-w-[64px] bg-amber-50/40">
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      className="input py-1 px-1.5 w-full text-right text-xs tabular-nums bg-amber-50/30 border-amber-200/60 focus:border-amber-400 focus:ring-amber-200/60"
                      value={s.hub || ''}
                      placeholder="—"
                      onChange={(e) =>
                        onCellChange(
                          r,
                          l.id,
                          'hub',
                          e.target.value === '' ? 0 : Number(e.target.value),
                        )
                      }
                    />
                  </td>
                </FragmentishCell>
              );
            })}
            <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-700 bg-slate-100/40">
              {rs.on > 0 ? rs.on.toFixed(1) : '·'}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-amber-900 bg-amber-50/40">
              {rs.hub > 0 ? rs.hub.toFixed(1) : '·'}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-brand-purple-700 bg-slate-100/40">
              {rs.total > 0 ? rs.total.toFixed(1) : '·'}
            </td>
          </tr>
        );
      })}
    </>
  );
}
